import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { defineStyles } from "@/internal/emotion";

const AVATAR_DIMENSION = "48px";

const styles = defineStyles({
  avatarBox: css({
    gap: "16px",
  }),
  avatarContainer: css({
    width: AVATAR_DIMENSION,
    height: AVATAR_DIMENSION,
    backgroundColor: theme.colors.neutral030,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  }),
  avatar: css({
    width: AVATAR_DIMENSION,
    height: AVATAR_DIMENSION,
  }),
  hiddenInput: css({
    display: "none",
  }),
});

namespace AvatarImageUpload {
  export interface Props {
    onChange: (file: File) => void;
    onClear?: VoidFunction;
    inputRef: React.RefObject<HTMLInputElement | null>;
    disable?: boolean;
    value?: string;
  }
}

export const AvatarImageUpload: React.FC<AvatarImageUpload.Props> =
  function AvatarFormItem({
    value,
    onChange,
    inputRef,
    onClear,
    disable = false,
  }) {
    const { t } = useTranslation();

    const handleOnChange = useHandler(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        onChange(file);
      },
    );

    const handleButtonClick = useHandler(() => {
      inputRef.current?.click();
    });

    return (
      <Stack direction="row" alignItems="center" css={styles.avatarBox}>
        <div css={styles.avatarContainer}>
          <Avatar src={value ?? ""} css={styles.avatar} />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleOnChange}
          disabled={disable}
          css={styles.hiddenInput}
        />
        <Stack gap="8px" direction="row">
          {!onClear ? null : (
            <Button variant="plain" onClick={onClear}>
              {t("resource.attachment.resetImage")}
            </Button>
          )}
          <Button variant="plain" loading={disable} onClick={handleButtonClick}>
            {t("resource.attachment.uploadImage")}
          </Button>
        </Stack>
      </Stack>
    );
  };
